import { getLocaleConfig } from "@/lib/email/config/locale-config";

/**
 * Check if a language code should use 24-hour time format
 * @param locale - Language code (e.g., "sv", "en", "da", "no")
 * @returns true if 24-hour format should be used
 */
export const shouldUse24HourFormat = (locale?: string): boolean => {
  if (!locale) return true; // Default to 24-hour for Nordic market
  const config = getLocaleConfig(locale);
  return config.timeFormat === "24h";
};

/**
 * Format time for display based on locale
 * @param time - Time string in format "HH:MM" (24-hour)
 * @param locale - Language code (e.g., "sv", "en", "da", "no"). Defaults to 24-hour format.
 * @returns Formatted time string (24-hour for Nordic, 12-hour AM/PM for English)
 */
export const formatTimeDisplay = (time: string, locale?: string): string => {
  const [hours, minutes] = time.split(":");
  const hour = parseInt(hours);

  // Use 24-hour format for Nordic languages, 12-hour for English
  if (shouldUse24HourFormat(locale)) {
    return `${hours}:${minutes}`;
  }

  // 12-hour format for English
  const period = hour >= 12 ? "PM" : "AM";
  const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
  return `${displayHour}:${minutes} ${period}`;
};

/**
 * Convert 12-hour time format to 24-hour format
 * @param time - Time string in format "H:MM AM/PM"
 * @returns Time string in 24-hour format "HH:MM"
 */
export const convertTo24Hour = (time: string): string => {
  const [timePart, period] = time.split(" ");
  const [hours, minutes] = timePart.split(":");
  let hour = parseInt(hours);

  if (period === "PM" && hour !== 12) {
    hour += 12;
  } else if (period === "AM" && hour === 12) {
    hour = 0;
  }

  return `${hour.toString().padStart(2, "0")}:${minutes}`;
};

/**
 * Format time range for display based on locale
 * @param startTime - Start time in 24-hour format
 * @param endTime - End time in 24-hour format
 * @param locale - Language code (e.g., "sv", "en", "da", "no"). Defaults to 24-hour format.
 * @returns Formatted time range string
 */
export const formatTimeRange = (
  startTime: string,
  endTime: string,
  locale?: string,
): string => {
  const use24Hour = shouldUse24HourFormat(locale);

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours);

    if (use24Hour) {
      return `${hours}:${minutes}`;
    }

    // 12-hour format
    const period = hour >= 12 ? "pm" : "am";
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}${minutes === "00" ? "" : `:${minutes}`}${period}`;
  };

  return `${formatTime(startTime)}-${formatTime(endTime)}`;
};

/**
 * Get time in minutes from start of day
 * @param time - Time string in format "HH:MM"
 * @returns Minutes from start of day
 */
export const timeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
};

/**
 * Convert minutes to time string
 * @param minutes - Minutes from start of day
 * @returns Time string in format "HH:MM"
 */
export const minutesToTime = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`;
};
